import React, { useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMicrophone, 
  faVideo, 
  faPause, 
  faPlay, 
  faStop,
  faRotateRight,
  faFloppyDisk
} from '@fortawesome/free-solid-svg-icons';
import { useMediaRecorder } from '../hooks/useMediaRecorder';
import { RecorderModalProps } from '../types';
import { formatTime } from '../utils/recordUtils';
import { AudioWaveVisualizer } from './AudioWaveVisualizer';
import '../styles/recorder.css';

export const RecorderModal: React.FC<RecorderModalProps> = ({
  isOpen,
  onClose,
  onSave,
  recordType,
  isUploading = false,
  errorMessage = null,
  onResetError,
}) => {
  const {
    isRecording,
    isPaused,
    recordedTime,
    stream,
    recordedBlob,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    resetRecording,
  } = useMediaRecorder(recordType);

  const MAX_RECORDING_TIME = 120; // 2 phút
  const remainingTime = MAX_RECORDING_TIME - recordedTime;
  const isNearLimit = remainingTime <= 30 && isRecording; // Cảnh báo khi còn 30 giây

  const videoRef = useRef<HTMLVideoElement>(null);
  const playbackRef = useRef<HTMLVideoElement | HTMLAudioElement>(null);

  useEffect(() => {
    if (stream && videoRef.current && recordType === 'video') {
      videoRef.current.srcObject = stream;
    }
  }, [stream, recordType]);

  useEffect(() => {
    if (!isOpen) {
      resetRecording();
    }
  }, [isOpen, resetRecording]);

  const handleClose = () => {
    resetRecording();
    onClose();
  };

  const handleSave = () => {
    if (recordedBlob) {
      onSave(recordedBlob, recordType);
    }
  };

  const handleStop = async () => {
    const blob = await stopRecording();
    if (blob && playbackRef.current) {
      playbackRef.current.src = URL.createObjectURL(blob);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="recorder-modal-overlay">
      <div className="recorder-modal">
        <div className="recorder-modal-header">
          <h3>
            <FontAwesomeIcon icon={recordType === 'video' ? faVideo : faMicrophone} style={{ marginRight: '8px' }} />
            {recordType === 'video' ? 'Ghi Video' : 'Ghi Âm'}
          </h3>
          <button className="close-btn" onClick={handleClose} disabled={isUploading}>
            ×
          </button>
        </div>

        <div className="recorder-modal-body">
          {/* Preview khi đang ghi video */}
          {isRecording && recordType === 'video' && (
            <div className="video-preview">
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                className="preview-video"
              />
              {isPaused && (
                <div className="paused-overlay">
                  <FontAwesomeIcon icon={faPause} style={{ marginRight: '8px' }} />
                  <span>Đã tạm dừng</span>
                </div>
              )}
            </div>
          )}

          {/* Audio visualization khi đang ghi âm */}
          {isRecording && recordType === 'audio' && (
            <div className="audio-preview">
              <div className="audio-visualizer">
                <div className={`recording-indicator-icon ${isPaused ? 'paused' : ''}`}>
                  {isPaused ? <FontAwesomeIcon icon={faPause} /> : <FontAwesomeIcon icon={faMicrophone} />}
                </div>
                <div className="audio-wave-container">
                  <AudioWaveVisualizer 
                    stream={stream} 
                    isActive={isRecording}
                    isPaused={isPaused}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Playback sau khi ghi xong */}
          {!isRecording && recordedBlob && (
            <div className="playback-container">
              {recordType === 'video' ? (
                <video
                  ref={playbackRef as React.RefObject<HTMLVideoElement>}
                  controls
                  className="playback-video"
                />
              ) : (
                <div className="audio-playback">
                  <audio
                    ref={playbackRef as React.RefObject<HTMLAudioElement>}
                    controls
                    className="playback-audio"
                  />
                </div>
              )}
            </div>
          )}

          {/* Timer */}
          {(isRecording || recordedBlob) && (
            <div className="timer">
              <span className={isRecording && !isPaused ? 'recording-dot' : ''}></span>
              <div>
                {formatTime(recordedTime)}
                {isRecording && (
                  <span style={{ 
                    marginLeft: '10px', 
                    fontSize: '14px',
                    color: isNearLimit ? '#dc3545' : '#6c757d'
                  }}>
                    / {formatTime(MAX_RECORDING_TIME)}
                  </span>
                )}
              </div>
              {isNearLimit && (
                <div style={{ 
                  fontSize: '12px', 
                  color: '#dc3545', 
                  marginTop: '5px',
                  fontWeight: 'bold'
                }}>
                  <i className="fas fa-exclamation-triangle me-1"></i>
                  Còn {remainingTime} giây
                </div>
              )}
            </div>
          )}

          {/* Controls */}
          <div className="recorder-controls">
            {!isRecording && !recordedBlob && (
              <button className="btn btn-primary btn-lg d-flex" onClick={startRecording}>
                <span>bắt đầu ghi</span>
              </button>
            )}

            {isRecording && !isPaused && (
              <>
                <button className="btn btn-warning" onClick={pauseRecording}>
                  <FontAwesomeIcon icon={faPause} className="btn-icon" />
                  Tạm dừng
                </button>
                <button className="btn btn-danger" onClick={handleStop}>
                  <FontAwesomeIcon icon={faStop} className="btn-icon" />
                  Dừng
                </button>
              </>
            )}

            {isRecording && isPaused && (
              <>
                <button className="btn btn-success" onClick={resumeRecording}>
                  <FontAwesomeIcon icon={faPlay} className="btn-icon" />
                  Tiếp tục
                </button>
                <button className="btn btn-danger" onClick={handleStop}>
                  <FontAwesomeIcon icon={faStop} className="btn-icon" />
                  Dừng
                </button>
              </>
            )}

            {!isRecording && recordedBlob && (
              <>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => {
                    onResetError?.();
                    resetRecording();
                  }}
                  disabled={isUploading}
                >
                  <FontAwesomeIcon icon={faRotateRight} className="btn-icon" />
                  Ghi lại
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={handleSave}
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <>
                      <span className="spinner-border spinner-border-sm btn-icon" role="status" aria-hidden="true"></span>
                      Đang lưu...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faFloppyDisk} className="btn-icon" />
                      Lưu vào nội dung
                    </>
                  )}
                </button>
              </>
            )}
          </div>
          {errorMessage && (
            <div className="mt-3 text-danger d-flex justify-content-center">
              {errorMessage}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

